/**
 * Utility functions for safe localStorage operations with error handling
 */

/**
 * Safely retrieves and parses a value from localStorage
 * @param {string} key - The localStorage key
 * @param {*} defaultValue - Default value to return if key doesn't exist or parsing fails
 * @returns {*} The parsed value or defaultValue
 */
export const getFromLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    
    if (item === null) {
      return defaultValue;
    }
    
    return JSON.parse(item);
  } catch (error) {
    console.error(`Failed to get item from localStorage (key: ${key}):`, error);
    return defaultValue;
  }
};

/**
 * Safely stringifies and saves a value to localStorage
 * @param {string} key - The localStorage key
 * @param {*} value - The value to save
 * @returns {boolean} True if save was successful, false otherwise
 */
export const setToLocalStorage = (key, value) => {
  try {
    const serialized = JSON.stringify(value);
    localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    console.error(`Failed to set item in localStorage (key: ${key}):`, error);
    
    if (error.name === 'QuotaExceededError') {
      console.error('LocalStorage quota exceeded. Data will not persist.');
    }
    
    return false;
  }
};

/**
 * Safely removes an item from localStorage
 * @param {string} key - The localStorage key to remove
 * @returns {boolean} True if removal was successful, false otherwise
 */
export const removeFromLocalStorage = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Failed to remove item from localStorage (key: ${key}):`, error);
    return false;
  }
};

/**
 * Validates user preferences against the expected schema
 * @param {Object} preferences - The preferences object to validate
 * @returns {boolean} True if preferences are valid, false otherwise
 */
export const validatePreferences = (preferences) => {
  if (!preferences || typeof preferences !== 'object') {
    return false;
  }

  const validViewModes = ['grid', 'list', 'compact'];
  const validLayoutDensities = ['comfortable', 'cozy', 'compact'];
  const validIconSizes = ['small', 'medium', 'large'];

  // Check required fields and their types
  const validations = [
    validViewModes.includes(preferences.viewMode),
    typeof preferences.cardSize === 'number' && preferences.cardSize >= 1 && preferences.cardSize <= 3,
    typeof preferences.cardSpacing === 'number' && preferences.cardSpacing >= 0.5 && preferences.cardSpacing <= 3,
    validLayoutDensities.includes(preferences.layoutDensity),
    typeof preferences.showDescriptions === 'boolean',
    typeof preferences.animationsEnabled === 'boolean',
    validIconSizes.includes(preferences.iconSize),
    Array.isArray(preferences.favorites),
    Array.isArray(preferences.pinnedCards),
  ];

  return validations.every(validation => validation === true);
};
